import { useEffect, useState } from 'react';
import { FaTimes, FaExternalLinkAlt, FaSpinner } from 'react-icons/fa';
import './IframeModal.css';

const IframeModal = ({
  isOpen,
  onClose,
  url,
  title,
  language = 'ar',
  size = 'large' // 'normal' or 'large' - default to large for better viewing
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Add class to prevent map interactions
      document.body.classList.add('iframe-modal-open');
      // Reset states when opening
      setIsLoading(true);
      setHasError(false);
      setIsOpening(false);
    } else {
      document.body.style.overflow = 'unset';
      // Remove class to allow map interactions
      document.body.classList.remove('iframe-modal-open');
      // Reset opening state when modal closes
      setIsOpening(false);
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('iframe-modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !url) return null;

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on modal content
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleOpenInBrowser = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple calls
    if (isOpening) {
      return;
    }


    if (!url) {
      console.error('No URL provided to open');
      return;
    }

    setIsOpening(true);

    try {
      // Create a temporary link element to ensure proper navigation
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error opening URL:', error);

      // Fallback: use window.open
      try {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          console.warn('Popup blocked or failed, please allow popups for this site');
          alert('Please allow popups for this site to open external links');
        }
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
      }
    } finally {
      // Reset the flag after a short delay
      setTimeout(() => {
        setIsOpening(false);
      }, 1000);
    }
  };

  const getModalTitle = () => {
    if (title) return title;
    
    // Try to determine title from URL or context
    if (url.includes('360') || url.includes('tour')) {
      return language === 'ar' ? 'جولة افتراضية 360°' : '360° Virtual Tour';
    } else if (url.includes('live') || url.includes('camera') || url.includes('stream')) {
      return language === 'ar' ? 'البث المباشر' : 'Live Stream';
    }
    
    return language === 'ar' ? 'عرض المحتوى' : 'View Content';
  };

  return (
    <div 
      className="iframe-modal-overlay" 
      onClick={handleOverlayClick}
    >
      <div
        className={`iframe-modal ${size} ${language === 'ar' ? 'rtl' : 'ltr'}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="iframe-modal-header">
          <div className="iframe-modal-title">
            <h3>{getModalTitle()}</h3>
          </div>
          <div className="iframe-modal-controls">
            {/* Open in Browser Button */}
            <button
              className="iframe-modal-external-btn"
              onClick={handleOpenInBrowser}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              title={language === 'ar' ? 'فتح في المتصفح' : 'Open in Browser'}
              type="button"
            >
              <FaExternalLinkAlt />
            </button>
            {/* Close Button */}
            <button
              className="iframe-modal-close-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              title={language === 'ar' ? 'إغلاق' : 'Close'}
              type="button"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="iframe-modal-content">
          {/* Loading Indicator */}
          {isLoading && (
            <div className="iframe-loading">
              <FaSpinner className="iframe-loading-spinner" />
              <p>{language === 'ar' ? 'جار التحميل...' : 'Loading...'}</p>
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div className="iframe-error">
              <p>{language === 'ar' ? 'حدث خطأ في تحميل المحتوى' : 'Error loading content'}</p>
              <button
                className="iframe-retry-btn"
                onClick={handleOpenInBrowser}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                type="button"
              >
                {language === 'ar' ? 'فتح في المتصفح' : 'Open in Browser'}
              </button>
            </div>
          )}

          {/* Iframe */}
          <iframe
            src={url}
            title={getModalTitle()}
            className="iframe-modal-frame"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
            style={{
              display: hasError ? 'none' : 'block'
            }}
          />
        </div>

     
      </div>
    </div>
  );
};

export default IframeModal;
